import { useCallback } from 'react';

import type { ItemId } from '@confluence/tree/entry-points/tree-types';
import {
	useContentTreeSelectionActions,
	useContentTreeSelection,
} from '@confluence/content-tree-actions/entry-points/store';

import { usePageTreeActions } from '../usePageTreeState';
import type { ContentTreeItem } from '../data-transformers';

type ItemWithParent = { id: ItemId; parentId?: ItemId };

export const useNestedDeselect = () => {
	const {
		deselectNestedContent,
		updateTotalCount,
		getSelectedDescendantCount,
		decrementAncestors,
		removeLoadingDescendant,
	} = useContentTreeSelectionActions();

	const { getState } = usePageTreeActions();
	const [{ loadingDescendantIds }] = useContentTreeSelection();

	const decrementAncestorCount = useCallback(
		(pageTreeItem: ContentTreeItem) => {
			const { pages, rootId } = getState();
			const ancestorContentIds = new Set<ItemId>();

			const getAncestorIds = (currItem) => {
				const parentId = currItem.parentId;

				if (parentId && parentId != rootId) {
					ancestorContentIds.add(currItem.parentId);
					getAncestorIds(pages[currItem.parentId]);
				}
			};

			getAncestorIds(pageTreeItem);
			decrementAncestors([...ancestorContentIds], pageTreeItem.id);
		},
		[getState, decrementAncestors],
	);

	const deselectContentAndDescendants = useCallback(
		(itemId: ItemId) => {
			const { pages } = getState();
			const descendants: ItemWithParent[] = [];
			const pageTreeItem = getState().pages[itemId];

			const selectedDescendantCount = getSelectedDescendantCount(itemId);
			decrementAncestorCount(pageTreeItem);
			const isLoadingDescendants = loadingDescendantIds.has(itemId);

			const traverse = (currItem: ContentTreeItem) => {
				descendants.push({ id: currItem.id, parentId: currItem.parentId ?? undefined });
				const children = currItem.children || [];
				for (const childId of children) {
					const child = pages[childId];
					if (child) {
						traverse(child);
					}
				}
			};
			traverse(pageTreeItem);

			deselectNestedContent(
				descendants.map(({ id }) => id),
				descendants.map(({ parentId }) => parentId ?? ''),
			);

			if (isLoadingDescendants) {
				// we don't want to show the loader if the item is deselected, even thought the query is still loading
				removeLoadingDescendant(itemId);
			} else {
				// only decrement count if descendent count is not being calculated, which mean the count has already been incremented
				updateTotalCount(-selectedDescendantCount - 1); // selectedDescendantCount does not include itself, hence the -1.
			}
		},
		[
			getState,
			getSelectedDescendantCount,
			decrementAncestorCount,
			loadingDescendantIds,
			deselectNestedContent,
			removeLoadingDescendant,
			updateTotalCount,
		],
	);

	return deselectContentAndDescendants;
};
